// PlatformCatalog.js
//
// Helpers for platform catalog + workspace adoption
// (see docs/appdeck-platform-catalog-v1-spec.md).

import { buildNodeFieldPatch, readNodeFieldRawValue } from 'utilities/NodeFieldData';

/**
 * @param {object|null|undefined} field
 * @returns {boolean}
 */
export function isWorkspaceField(field) {
  return field?.workspaceField === true;
}

/**
 * @param {object|null|undefined} field
 * @returns {boolean}
 */
export function isCatalogField(field) {
  return Boolean(field) && !isWorkspaceField(field);
}

/**
 * @param {object[]} [fields]
 * @returns {object[]}
 */
export function getCatalogSchemaFields(fields = []) {
  return (Array.isArray(fields) ? fields : []).filter(isCatalogField);
}

/**
 * @param {object[]} [fields]
 * @returns {object[]}
 */
export function getWorkspaceSchemaFields(fields = []) {
  return (Array.isArray(fields) ? fields : []).filter(isWorkspaceField);
}

/**
 * @param {object|null|undefined} node
 * @returns {string}
 */
export function getNodeCatalogId(node) {
  return String(node?.catalogId || '').trim();
}

/**
 * @param {object|null|undefined} node
 * @returns {boolean}
 */
export function isCatalogAdoptedNode(node) {
  return Boolean(getNodeCatalogId(node));
}

/**
 * @param {string} catalogId
 * @returns {string}
 */
export function buildCatalogOptionValue(catalogId) {
  return `__catalog__${String(catalogId || '').trim()}`;
}

/**
 * @param {string} value
 * @returns {string}
 */
export function parseCatalogOptionValue(value) {
  const raw = String(value || '').trim();
  if (!raw.startsWith('__catalog__')) return '';
  return raw.slice('__catalog__'.length).trim();
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isCatalogOptionValue(value) {
  return Boolean(parseCatalogOptionValue(value));
}

/**
 * Live-read: overlay catalog-owned field values (and title) onto a node for display.
 *
 * @param {object|null|undefined} node
 * @param {object|null|undefined} catalogEntry
 * @param {object|null|undefined} typeDef
 * @returns {object|null|undefined}
 */
export function applyCatalogLiveRead(node, catalogEntry, typeDef = null) {
  if (!node || typeof node !== 'object') return node;
  if (!catalogEntry || typeof catalogEntry !== 'object') return node;

  const typeKey = String(typeDef?.key || node.type || catalogEntry.typeKey || '').trim();
  const fields = Array.isArray(typeDef?.schema?.fields)
    ? typeDef.schema.fields
    : (Array.isArray(catalogEntry.schemaFields) ? catalogEntry.schemaFields : []);

  let next = { ...node };
  const catalogTitle = String(catalogEntry.title || '').trim();
  if (catalogTitle) {
    next = { ...next, title: catalogTitle };
  }

  const coverPath = String(catalogEntry.coverStoragePath || catalogEntry.coverUrl || '').trim();
  if (coverPath) {
    next = {
      ...next,
      coverUrl: coverPath,
      coverStoragePath: coverPath,
    };
  }

  getCatalogSchemaFields(fields).forEach((field) => {
    if (String(field?.type || '').trim().toLowerCase() === 'cover') return;
    const value = readNodeFieldRawValue(catalogEntry, field, typeKey);
    if (value === undefined) return;
    const patch = buildNodeFieldPatch(next, field, typeKey, value);
    if (patch) next = { ...next, ...patch };
  });

  // Catalog entries may also stash bag values under `data` for simplicity.
  if (catalogEntry.data && typeof catalogEntry.data === 'object' && typeKey) {
    const dataKey = `data_${typeKey.toLowerCase()}`;
    const catalogBag = catalogEntry[dataKey] || catalogEntry.data;
    if (catalogBag && typeof catalogBag === 'object') {
      const existing = next[dataKey] && typeof next[dataKey] === 'object' ? next[dataKey] : {};
      const mergedBag = { ...existing };
      getCatalogSchemaFields(fields).forEach((field) => {
        const name = String(field?.name || '').trim();
        if (!name) return;
        if (catalogBag[name] !== undefined) mergedBag[name] = catalogBag[name];
      });
      next = { ...next, [dataKey]: mergedBag };
    }
  }

  return next;
}

/**
 * Build materialized field values from a catalog entry onto a plain updates object
 * (for detach-on-delete / converting pointer → custom).
 *
 * @param {object} catalogEntry
 * @param {object|null|undefined} typeDef
 * @returns {object}
 */
export function buildCatalogMaterializeUpdates(catalogEntry, typeDef = null) {
  const typeKey = String(typeDef?.key || catalogEntry?.typeKey || '').trim();
  const fields = Array.isArray(typeDef?.schema?.fields) ? typeDef.schema.fields : [];
  const updates = {
    catalogId: null,
  };

  const title = String(catalogEntry?.title || '').trim();
  if (title) updates.title = title;

  const coverPath = String(catalogEntry?.coverStoragePath || catalogEntry?.coverUrl || '').trim();
  if (coverPath) {
    updates.coverUrl = coverPath;
    updates.coverStoragePath = coverPath;
  }

  if (!typeKey) return updates;

  const dataKey = `data_${typeKey.toLowerCase()}`;
  const bag = {};

  getCatalogSchemaFields(fields).forEach((field) => {
    const name = String(field?.name || '').trim();
    if (!name) return;
    let value = readNodeFieldRawValue(catalogEntry, field, typeKey);
    if (value === undefined && catalogEntry?.data && typeof catalogEntry.data === 'object') {
      value = catalogEntry.data[name];
    }
    if (value === undefined && catalogEntry?.[dataKey] && typeof catalogEntry[dataKey] === 'object') {
      value = catalogEntry[dataKey][name];
    }
    if (value !== undefined) bag[name] = value;
  });

  if (Object.keys(bag).length) {
    updates[dataKey] = bag;
  }

  return updates;
}

/**
 * Normalize a title for catalog seed matching.
 *
 * @param {string} title
 * @returns {string}
 */
export function normalizeCatalogTitle(title) {
  return String(title || '').trim().toLowerCase().replace(/\s+/g, ' ');
}
